'use client';

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import { cn } from '@/lib/utils';
import { ProfileLink } from '@/components/profile/ProfileLink';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useCommunityMembers } from '@/hooks/use-community-members';
import { useUser } from '@/hooks/use-user';
import { useCommunityContext } from '@/providers/community-context';
import axiosInstance from '@/lib/axios';
import {
  Briefcase,
  ChartColumnIncreasing,
  Download,
  FileHeartIcon,
  Handshake,
  Info,
  Loader2,
  Mail,
  MapPin,
  MessageSquare,
  NotebookPen,
  Phone,
  Pencil,
  Plus,
  Search,
  Tag,
  Users,
} from 'lucide-react';
import { HandsPrayingIcon } from '@phosphor-icons/react';
import { triggerBlobFileDownload } from '@/lib/trigger-download';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MemberNoteDialog } from './components/MemberNoteDialog';
import { MemberTagDialog } from './components/MemberTagDialog';
import { formatBusinessCategoryLabel } from '@/lib/business-category';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import type { Member } from '@/types';
import { formatDisplayPhone, phoneTelHref } from '@/lib/phone-utils';
import { toast } from 'sonner';

const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) || window.innerWidth < 768;
};

function NotOnboardedShipIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M10 2.5h4v2h-4z" />
      <path d="M12 1.5v1" />
      <path d="M7.5 4.5h9v3h-9z" />
      <path d="M5.5 7.5h13v2.5l-2.5 5h-8l-2.5-5z" />
      <path d="M12 7.5v7.5" />
      <circle cx="8.5" cy="9" r="0.5" fill="currentColor" />
      <circle cx="12" cy="9" r="0.5" fill="currentColor" />
      <circle cx="15.5" cy="9" r="0.5" fill="currentColor" />
      <path d="M2 18.5c1.7-1 3.3-1 5 0s3.3 1 5 0 3.3-1 5 0 3.3 1 5 0" />
      <path d="M2 21.5c1.7-1 3.3-1 5 0s3.3 1 5 0 3.3-1 5 0 3.3 1 5 0" />
    </svg>
  );
}

function ActiveBoatIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M12 1c-.8.8.8 1.4 0 2.2" strokeWidth="1.8" />
      <path d="M10.5 3.2h3v3.8h-3z" fill="currentColor" stroke="none" />
      <path d="M5.5 7h11.5l3 4.5H4.5l1-4.5z" />
      <path d="M1 12.5l2-1h18l2 1-2.5 5.5H3.5L1 12.5z" />
      <path d="M2 19.5c1.7-.8 3.3-.8 5 0s3.3.8 5 0 3.3-.8 5 0 3.3.8 5 0" />
      <path d="M2 22c1.7-.8 3.3-.8 5 0s3.3.8 5 0 3.3-.8 5 0 3.3.8 5 0" />
    </svg>
  );
}

function InactiveAnchorIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="0.5"
      className={className}
    >
      <path d="M12 2a2.5 2.5 0 0 0-1 4.79V8H8.5a.75.75 0 0 0 0 1.5H11v7.05A6.51 6.51 0 0 1 5.5 10a.75.75 0 0 0-1.5 0 8.01 8.01 0 0 0 7 7.94V20H9.5a.75.75 0 0 0 0 1.5h5a.75.75 0 0 0 0-1.5H13v-2.06a8.01 8.01 0 0 0 7-7.94.75.75 0 0 0-1.5 0 6.51 6.51 0 0 1-5.5 6.55V9.5h2.5a.75.75 0 0 0 0-1.5H13V6.79A2.5 2.5 0 0 0 12 2zm0 1.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
    </svg>
  );
}

function getMemberActivityState(member: Member) {
  if (member.isOnboarded === false) {
    if (member.lastAccessedAt) {
      return {
        key: 'form-pending',
        label: 'Visited, onboarding form pending',
        Icon: NotOnboardedShipIcon,
        className: 'text-amber-500',
        badgeBg: 'bg-amber-500/10 border-amber-500/30 text-amber-600',
      };
    }
    return {
      key: 'never-visited',
      label: 'Never visited community',
      Icon: NotOnboardedShipIcon,
      className: 'text-muted-foreground/50',
      badgeBg: 'bg-muted border-border/80 text-muted-foreground',
    };
  }
  if (member.usedInLast30Days) {
    return {
      key: 'active',
      label: 'Used in last 30 days',
      Icon: ActiveBoatIcon,
      className: 'text-emerald-600',
      badgeBg: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700',
    };
  }
  return {
    key: 'inactive',
    label: 'Not used in last 30 days',
    Icon: InactiveAnchorIcon,
    className: 'text-muted-foreground',
    badgeBg: 'bg-muted border-border/80 text-muted-foreground',
  };
}

interface MembersViewProps {
  communityId: string;
}

export function MembersView({ communityId }: MembersViewProps) {
  const navigate = useNavigate();
  const [page, setPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1).withOptions({ shallow: false })
  );
  const [searchQuery, setSearchQuery] = useQueryState(
    'search',
    parseAsString.withDefault('').withOptions({ history: 'replace', clearOnDefault: true })
  );
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [legendDialogOpen, setLegendDialogOpen] = useState(false);
  const { members, currentUserId, metadata, isLoading, mutate } = useCommunityMembers(communityId, {
    page: page,
    limit: 100,
    search: debouncedSearch,
  });

  const { user } = useUser();
  const currentUserMember = members.find((m) => m.id === currentUserId);
  const { community: currentCommunity } = useCommunityContext();
  const userCommunity = user?.communities.find((c) => c.id === communityId);
  const userPermissions = userCommunity?.permissions || [];
  const userRoles = userCommunity?.roles || [];

  const isAdmin =
    userRoles.some((role) => ['ADMIN', 'CREATOR', 'OWNER'].includes(role.toUpperCase())) ||
    (currentUserMember?.roles?.some((role) =>
      ['ADMIN', 'CREATOR', 'OWNER'].includes(role.toUpperCase())
    ) ??
      false);

  const canViewActiveUserStatus = isAdmin || userPermissions.includes('view_active_user_status');
  const isNotesEnabled = currentCommunity?.isNotesEnabled ?? true;
  const isPointsEnabled = currentCommunity?.isPointsEnabled ?? false;

  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [createPostMember, setCreatePostMember] = useState<Member | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  const handleDownloadCSV = async (includeOnboardingDetails: boolean = false) => {
    try {
      const res = await axiosInstance.get(
        `/api/communities/${communityId}/members/csv?onboarding=${includeOnboardingDetails}`,
        {
          responseType: 'blob',
        }
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      triggerBlobFileDownload(
        blob,
        `${currentCommunity?.name.replace(/\s+/g, '_') || 'members'}_members.csv`
      );
      toast.success('CSV downloaded successfully');
    } catch (error) {
      console.error('Failed to download CSV:', error);
      toast.error('Failed to download CSV');
    }
  };

  const STATUS_OPTIONS = [
    {
      key: 'never-visited',
      label: 'Never Visited',
      description: 'User has not opened or visited the community yet.',
      Icon: NotOnboardedShipIcon,
      iconColor: 'text-slate-500',
      iconBg: 'bg-slate-100',
      cardBorder: 'border-slate-200 bg-slate-50/60',
      countColor: 'text-slate-700',
    },
    {
      key: 'form-pending',
      label: 'Form Pending',
      description:
        'User has visited the community, but onboarding questions/form are yet to be submitted.',
      Icon: NotOnboardedShipIcon,
      iconColor: 'text-amber-500',
      iconBg: 'bg-amber-500/10',
      cardBorder: 'border-amber-500/20 bg-amber-500/5',
      countColor: 'text-amber-600',
    },
    {
      key: 'all',
      label: 'Total Members',
      description: 'Total number of members in this community.',
      Icon: Users,
      iconColor: 'text-sky-600',
      iconBg: 'bg-sky-500/10',
      cardBorder: 'border-sky-500/20 bg-sky-500/5',
      countColor: 'text-sky-600',
    },
    {
      key: 'active',
      label: 'Active Members',
      description: 'Onboarded and active in the community within the last 30 days.',
      Icon: ActiveBoatIcon,
      iconColor: 'text-emerald-600',
      iconBg: 'bg-emerald-500/10',
      cardBorder: 'border-emerald-500/20 bg-emerald-500/5',
      countColor: 'text-emerald-600',
    },
    {
      key: 'inactive',
      label: 'Inactive',
      description: 'Onboarded member with no activity recorded in the last 30 days.',
      Icon: InactiveAnchorIcon,
      iconColor: 'text-slate-500',
      iconBg: 'bg-slate-100',
      cardBorder: 'border-slate-200 bg-slate-50/60',
      countColor: 'text-slate-700',
    },
  ] as const;

  const activityCounts = useMemo(() => {
    const counts = {
      'never-visited': 0,
      'form-pending': 0,
      all: metadata?.totalCount ?? members.length,
      active: 0,
      inactive: 0,
    };
    for (const member of members) {
      const state = getMemberActivityState(member);
      const key = state.key as keyof typeof counts;
      if (counts[key] !== undefined && key !== 'all') {
        counts[key]++;
      }
    }
    return counts;
  }, [members, metadata?.totalCount]);

  const filteredMembers = useMemo(() => {
    if (selectedStatus === 'all') return members;
    return members.filter((m) => getMemberActivityState(m).key === selectedStatus);
  }, [members, selectedStatus]);

  const [noteId, setNoteId] = useQueryState(
    'note',
    parseAsString.withOptions({ history: 'push', shallow: false })
  );
  const noteMember = members.find((m) => m.id === noteId);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, setPage]);

  useEffect(() => {
    if (selectedMember) {
      window.history.pushState({ modalOpen: true }, '');
    }

    const handlePopState = (_event: PopStateEvent) => {
      if (selectedMember) {
        setSelectedMember(null);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedMember]);

  const handleClose = () => {
    if (selectedMember) {
      setSelectedMember(null);
      if (window.history.state?.modalOpen) {
        window.history.back();
      }
    }
  };

  const handleWhatsApp = (phone: string) => {
    const sanitized = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${sanitized}`, '_blank');
  };

  const handleCall = (phone: string) => {
    const telHref = phoneTelHref(phone);
    if (!telHref) return;
    if (isMobile()) {
      window.open(telHref, '_self');
    } else {
      navigator.clipboard.writeText(formatDisplayPhone(phone) || phone);
      toast.success('Phone number copied to clipboard!');
    }
  };
  const handleEmail = (email: string) => {
    if (isMobile()) {
      window.open(`mailto:${email}`, '_self');
    } else {
      navigator.clipboard.writeText(email);
      toast.success('Email address copied to clipboard!');
    }
  };

  const renderMemberCardContent = (member: Member) => {
    const addressParts = member.userLocation || { district: '', state: '' };
    const primaryRole = member.roles?.[0] || 'Member';
    const isCurrentUser = member.id === currentUserId;
    const displayTag = member.tag || primaryRole;
    const hasCustomTag = !!member.tag;
    const activityState = getMemberActivityState(member);
    const ActivityIcon = activityState.Icon;

    return (
      <div className="p-4 flex gap-4 h-full">
        <div className="shrink-0 flex flex-col items-center">
          <ProfileLink
            userId={member.id}
            image={member.image}
            name={member.name}
            showName={false}
            avatarSize="lg"
            rounded="xl"
            className="shadow-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="flex items-center justify-center gap-1.5 mt-2 hover:opacity-80 transition-opacity cursor-pointer group/pts focus:outline-none"
            title={`${activityState.label} - Click to view status guide`}
            onClick={(e) => {
              e.stopPropagation();
              setLegendDialogOpen(true);
            }}
          >
            <ActivityIcon className={`h-6 w-6 shrink-0 ${activityState.className}`} />
            {isPointsEnabled && (
              <div className="px-1.5 py-0.5 rounded-md border border-border/80 bg-background text-xs font-bold text-foreground text-center min-w-[24px] leading-tight shadow-2xs group-hover/pts:border-primary/50 transition-colors">
                {member.totalPoints ?? 0}
              </div>
            )}
          </button>
        </div>

        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex justify-between items-start">
            <h3 className="text-lg font-bold text-foreground truncate group-hover:text-primary transition-colors">
              {member.name}
            </h3>
            <div className="flex items-center gap-0.5 -mt-1 -mr-2 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-primary transition-colors"
                title={`Create post for ${member.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCreatePostMember(member);
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
              {isNotesEnabled && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-primary transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNoteId(member.id);
                  }}
                >
                  <NotebookPen className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {member.businessName && (
            <p className="text-primary font-bold text-sm -mt-0.5 truncate">{member.businessName}</p>
          )}

          <div className="mt-1 flex items-center justify-between text-xs font-medium text-muted-foreground">
            <div className="flex items-center gap-1">
              {isCurrentUser && !hasCustomTag ? (
                <button
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/5 border border-dashed border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTagDialogOpen(true);
                  }}
                >
                  <Tag className="h-3 w-3" />
                  <span className="text-[11px] font-semibold">Add tag</span>
                </button>
              ) : (
                <>
                  <span className="capitalize">{displayTag.toLowerCase()}</span>
                  {isCurrentUser && hasCustomTag && (
                    <button
                      className="ml-0.5 p-0.5 rounded-full hover:bg-muted transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTagDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-3 w-3 text-muted-foreground" />
                    </button>
                  )}
                </>
              )}
            </div>
            {(addressParts.district || addressParts.state) && (
              <div className="text-muted-foreground">
                {addressParts.district && addressParts.district}
                {addressParts.district && addressParts.state && ' | '}
                {addressParts.state && addressParts.state}
              </div>
            )}
          </div>

          <div className="mt-auto pt-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground">
              {formatDisplayPhone(member.phone)}
            </span>

            <div className="flex items-center gap-2">
              <button
                className="p-1.5 rounded-full bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCall(member.phone);
                }}
              >
                <Phone className="h-4 w-4" />
              </button>
              <button
                className="p-1.5 rounded-full bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEmail(member.email);
                }}
              >
                <Mail className="h-4 w-4" />
              </button>
              <button
                className="p-1.5 rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors shadow-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleWhatsApp(member.phone);
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4 fill-current"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const groupedItems: Array<
    { type: 'single'; member: Member } | { type: 'spouse-pair'; member1: Member; member2: Member }
  > = [];

  const seenIds = new Set<string>();
  const isSpouseEnabled = currentCommunity?.enableSpouse ?? false;

  for (const member of filteredMembers) {
    if (seenIds.has(member.id)) continue;

    const spouseId = member.spouseId;
    const spouse =
      isSpouseEnabled && spouseId ? filteredMembers.find((m) => m.id === spouseId) : null;

    if (spouse && !seenIds.has(spouse.id)) {
      groupedItems.push({
        type: 'spouse-pair',
        member1: member,
        member2: spouse,
      });
      seenIds.add(member.id);
      seenIds.add(spouse.id);
    } else {
      groupedItems.push({
        type: 'single',
        member: member,
      });
      seenIds.add(member.id);
    }
  }

  return (
    <div className="space-y-6 md:space-y-8 pb-6">
      <div className="flex items-center justify-between gap-4 w-full">
        <div className="flex items-center gap-2 flex-nowrap">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight whitespace-nowrap">
            Community Members
          </h1>
          <button
            type="button"
            onClick={() => setLegendDialogOpen(true)}
            className="p-1 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors cursor-pointer shrink-0"
            title="Activity & Onboarding Status Guide"
          >
            <Info className="h-5 w-5" />
          </button>
        </div>
        {isAdmin && (
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleDownloadCSV(false)}
            className="h-9 w-9 rounded-xl shrink-0 text-muted-foreground hover:text-primary transition-colors cursor-pointer border-border/80 bg-white shadow-2xs"
            title="Download CSV"
          >
            <Download className="h-4 w-4" />
          </Button>
        )}
      </div>

      {canViewActiveUserStatus && (
        <div className="grid grid-cols-5 gap-2 sm:gap-3.5 w-full pt-2 pb-1.5">
          {STATUS_OPTIONS.map((opt) => {
            const OptionIcon = opt.Icon;
            const isSelected = selectedStatus === opt.key;
            const count = activityCounts[opt.key as keyof typeof activityCounts];
            const formattedCount = count > 9999 ? '9999+' : count;
            return (
              <button
                key={opt.key}
                type="button"
                title={`${opt.label} (${formattedCount})`}
                onClick={() => setSelectedStatus(opt.key)}
                className={cn(
                  'flex flex-col items-center justify-center gap-1 w-full h-14 sm:h-16 px-2 rounded-md border transition-all duration-200 cursor-pointer hover:scale-[1.03] active:scale-95',
                  isSelected
                    ? 'bg-sky-500 text-white border-sky-500 shadow-md shadow-sky-500/30'
                    : 'bg-card text-foreground border-border/60 shadow-sm hover:shadow-md hover:border-border'
                )}
              >
                <OptionIcon
                  className={cn(
                    'h-5 w-5 sm:h-6 sm:w-6 shrink-0',
                    isSelected ? 'text-white' : opt.iconColor
                  )}
                />
                <span
                  className={cn(
                    'text-xs sm:text-sm font-extrabold leading-none',
                    isSelected ? 'text-white' : 'text-slate-900'
                  )}
                >
                  {formattedCount}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="w-full">
        <Input
          type="search"
          placeholder="Search members..."
          icon={<Search className="h-5 w-5" />}
          uiSize="lg"
          className="rounded-xl shadow-sm focus-visible:ring-primary/20"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {groupedItems.length === 0 ? (
            <div className="text-center py-12 px-4 border-2 border-dashed rounded-2xl bg-card/50 space-y-3">
              <p className="text-muted-foreground text-sm font-medium">
                {searchQuery
                  ? 'No members found matching your search.'
                  : selectedStatus !== 'all'
                    ? `No members found for "${STATUS_OPTIONS.find((o) => o.key === selectedStatus)?.label || selectedStatus}".`
                    : 'No members found in this community.'}
              </p>
              {selectedStatus !== 'all' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedStatus('all')}
                  className="rounded-xl text-xs"
                >
                  Clear Status Filter
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {groupedItems.map((item) => {
                if (item.type === 'spouse-pair') {
                  const member1 = item.member1;
                  const member2 = item.member2;
                  const isCurrentUser1 = member1.id === currentUserId;
                  const isCurrentUser2 = member2.id === currentUserId;

                  return (
                    <Card
                      key={`${member1.id}-${member2.id}`}
                      className={`md:col-span-2 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl border bg-card relative divide-y md:divide-y-0 md:divide-x divide-border flex flex-col md:flex-row ${
                        isCurrentUser1 || isCurrentUser2
                          ? 'border-primary/30 ring-1 ring-primary/10'
                          : 'border-border'
                      }`}
                    >
                      <div
                        className="flex-1 cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => navigate(`/${communityId}/u/${member1.id}`)}
                      >
                        {renderMemberCardContent(member1)}
                      </div>

                      <div
                        className="flex-1 cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => navigate(`/${communityId}/u/${member2.id}`)}
                      >
                        {renderMemberCardContent(member2)}
                      </div>
                    </Card>
                  );
                } else {
                  const member = item.member;
                  const isCurrentUser = member.id === currentUserId;

                  return (
                    <Card
                      key={member.id}
                      className={`group relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl cursor-pointer active:scale-[0.99] ${
                        isCurrentUser ? 'border-primary/30 ring-1 ring-primary/10' : 'border-border'
                      }`}
                      onClick={() => navigate(`/${communityId}/u/${member.id}`)}
                    >
                      {renderMemberCardContent(member)}
                    </Card>
                  );
                }
              })}
            </div>
          )}

          {metadata && metadata.totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="text-sm font-medium">
                Page {page} of {metadata.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(metadata.totalPages, p + 1))}
                disabled={page === metadata.totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog open={!!selectedMember} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="sm:max-w-[425px] max-h-[90dvh] p-0 gap-0 overflow-hidden flex flex-col">
          <DialogHeader className="p-6 pb-2 flex-shrink-0">
            <div className="flex flex-col items-center gap-4">
              <ProfileLink
                userId={selectedMember?.id}
                image={selectedMember?.image}
                name={selectedMember?.name}
                showName={false}
                avatarSize="xl"
              />
              <div className="text-center flex flex-col items-center">
                <DialogTitle className="text-2xl">{selectedMember?.name}</DialogTitle>
                {selectedMember?.businessName && (
                  <p className="text-primary font-medium">{selectedMember.businessName}</p>
                )}
                {selectedMember && (
                  <button
                    type="button"
                    className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-lg border border-border bg-muted/40 hover:bg-muted/70 transition-colors cursor-pointer text-left"
                    title="Click to view status guide"
                    onClick={() => setLegendDialogOpen(true)}
                  >
                    {(() => {
                      const activity = getMemberActivityState(selectedMember);
                      const DialogActivityIcon = activity.Icon;
                      return (
                        <DialogActivityIcon className={`h-6 w-6 shrink-0 ${activity.className}`} />
                      );
                    })()}
                    {isPointsEnabled && (
                      <span className="text-xs font-bold text-foreground">
                        {selectedMember.totalPoints ?? 0} pts
                      </span>
                    )}
                    <span className="text-[11px] text-muted-foreground ml-1">
                      ({getMemberActivityState(selectedMember).label})
                    </span>
                  </button>
                )}
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6 pt-4">
            <div className="grid gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3 group">
                  <div className="p-2 rounded-full bg-muted group-hover:bg-primary/10 transition-colors">
                    <Phone className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground uppercase font-semibold">
                      Phone
                    </span>
                    <a
                      href={phoneTelHref(selectedMember?.phone)}
                      className="text-sm font-medium hover:text-primary transition-colors cursor-pointer"
                      onClick={(e) => {
                        if (!isMobile()) {
                          e.preventDefault();
                          handleCall(selectedMember?.phone || '');
                        }
                      }}
                    >
                      {formatDisplayPhone(selectedMember?.phone)}
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3 group">
                  <div className="p-2 rounded-full bg-muted group-hover:bg-primary/10 transition-colors">
                    <Mail className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground uppercase font-semibold">
                      Email
                    </span>
                    <a
                      href={`mailto:${selectedMember?.email}`}
                      className="text-sm font-medium hover:text-primary transition-colors break-all cursor-pointer"
                      onClick={(e) => {
                        if (!isMobile()) {
                          e.preventDefault();
                          handleEmail(selectedMember?.email || '');
                        }
                      }}
                    >
                      {selectedMember?.email}
                    </a>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  className="w-full gap-2 h-11"
                  onClick={() => selectedMember && handleCall(selectedMember.phone)}
                >
                  <Phone className="h-4 w-4" />
                  Call
                </Button>
                <Button
                  className="w-full gap-2 h-11 bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => selectedMember && handleWhatsApp(selectedMember.phone)}
                >
                  <MessageSquare className="h-4 w-4" />
                  WhatsApp
                </Button>
              </div>

              {selectedMember?.businessName && (
                <>
                  <div className="pt-2">
                    <h4 className="text-sm font-semibold mb-4 flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-primary" />
                      Business Details
                    </h4>
                    <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground uppercase font-semibold">
                          Company
                        </span>
                        <span className="text-sm font-medium">{selectedMember.businessName}</span>
                      </div>

                      {formatBusinessCategoryLabel(
                        selectedMember.businessCategory,
                        selectedMember.subCategory,
                        selectedMember.selfDefinedCategory
                      ) && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase font-semibold">
                            Category
                          </span>
                          <span className="text-sm font-medium">
                            {formatBusinessCategoryLabel(
                              selectedMember.businessCategory,
                              selectedMember.subCategory,
                              selectedMember.selfDefinedCategory
                            )}
                          </span>
                        </div>
                      )}

                      {selectedMember.businessAddress && (
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground uppercase font-semibold">
                            Address
                          </span>
                          <div className="flex items-start gap-2 mt-0.5">
                            <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                            <span className="text-sm leading-relaxed">
                              {typeof selectedMember.businessAddress === 'string'
                                ? selectedMember.businessAddress
                                : [
                                    selectedMember.businessAddress.addressLine1,
                                    selectedMember.businessAddress.addressLine2,
                                    selectedMember.businessAddress.landmark,
                                    selectedMember.businessAddress.district,
                                    selectedMember.businessAddress.state,
                                    selectedMember.businessAddress.pinCode,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                            </span>
                          </div>
                        </div>
                      )}

                      {(selectedMember.businessPhone || selectedMember.businessEmail) && (
                        <div className="pt-2 border-t space-y-3">
                          {selectedMember.businessPhone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <a
                                href={phoneTelHref(selectedMember.businessPhone)}
                                className="text-sm hover:text-primary transition-colors cursor-pointer"
                                onClick={(e) => {
                                  if (!isMobile()) {
                                    e.preventDefault();
                                    handleCall(selectedMember.businessPhone || '');
                                  }
                                }}
                              >
                                {formatDisplayPhone(selectedMember.businessPhone)}
                              </a>
                            </div>
                          )}
                          {selectedMember.businessEmail && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <a
                                href={`mailto:${selectedMember.businessEmail}`}
                                className="text-sm hover:text-primary transition-colors break-all cursor-pointer"
                                onClick={(e) => {
                                  if (!isMobile()) {
                                    e.preventDefault();
                                    handleEmail(selectedMember.businessEmail || '');
                                  }
                                }}
                              >
                                {selectedMember.businessEmail}
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {noteMember && (
        <MemberNoteDialog
          isOpen={!!noteMember}
          onClose={() => setNoteId(null)}
          communityId={communityId}
          member={noteMember}
        />
      )}

      <Dialog open={!!createPostMember} onOpenChange={(open) => !open && setCreatePostMember(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              Create Post for {createPostMember?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            {currentCommunity?.isLeadEnabled && (
              <button
                type="button"
                onClick={() => {
                  const targetMember = createPostMember;
                  setCreatePostMember(null);
                  navigate(`/${communityId}/create/lead?memberId=${targetMember?.id}&from=members`);
                }}
                className="flex flex-col items-center justify-center gap-2.5 p-4 rounded-2xl border border-border bg-card hover:border-primary/60 hover:bg-primary/5 transition-all cursor-pointer text-center"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <ChartColumnIncreasing className="h-6 w-6" />
                </div>
                <span className="text-xs font-semibold text-foreground">Requirement</span>
              </button>
            )}
            {currentCommunity?.isThankYouNoteEnabled && (
              <button
                type="button"
                onClick={() => {
                  const targetMember = createPostMember;
                  setCreatePostMember(null);
                  navigate(
                    `/${communityId}/create/thank-you?memberId=${targetMember?.id}&from=members`
                  );
                }}
                className="flex flex-col items-center justify-center gap-2.5 p-4 rounded-2xl border border-border bg-card hover:border-primary/60 hover:bg-primary/5 transition-all cursor-pointer text-center"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <HandsPrayingIcon className="h-6 w-6" />
                </div>
                <span className="text-xs font-semibold text-foreground">Money Earned</span>
              </button>
            )}
            {currentCommunity?.isMeetupEnabled && (
              <button
                type="button"
                onClick={() => {
                  const targetMember = createPostMember;
                  setCreatePostMember(null);
                  navigate(
                    `/${communityId}/create/meetup?memberId=${targetMember?.id}&from=members`
                  );
                }}
                className="flex flex-col items-center justify-center gap-2.5 p-4 rounded-2xl border border-border bg-card hover:border-primary/60 hover:bg-primary/5 transition-all cursor-pointer text-center"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Handshake className="h-6 w-6" />
                </div>
                <span className="text-xs font-semibold text-foreground">Meet Up</span>
              </button>
            )}
            {currentCommunity?.isTestimonialEnabled && (
              <button
                type="button"
                onClick={() => {
                  const targetMember = createPostMember;
                  setCreatePostMember(null);
                  navigate(
                    `/${communityId}/create/testimonial?memberId=${targetMember?.id}&from=members`
                  );
                }}
                className="flex flex-col items-center justify-center gap-2.5 p-4 rounded-2xl border border-border bg-card hover:border-primary/60 hover:bg-primary/5 transition-all cursor-pointer text-center"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <FileHeartIcon className="h-6 w-6" />
                </div>
                <span className="text-xs font-semibold text-foreground">Testimonials</span>
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <MemberTagDialog
        isOpen={tagDialogOpen}
        onClose={() => setTagDialogOpen(false)}
        communityId={communityId}
        currentTag={currentUserMember?.tag}
        onSaved={() => mutate()}
      />

      <Dialog open={legendDialogOpen} onOpenChange={setLegendDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              Activity & Onboarding Status Guide
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex items-start gap-3 p-2.5 rounded-lg border border-border/70 bg-muted/20">
              <div className="p-2 rounded-md bg-muted/60 shrink-0 mt-0.5">
                <NotOnboardedShipIcon className="h-6 w-6 text-muted-foreground/50" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-foreground">Never Visited</span>
                <span className="text-xs text-muted-foreground">
                  User has not opened or visited the community yet.
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3 p-2.5 rounded-lg border border-amber-500/20 bg-amber-500/5">
              <div className="p-2 rounded-md bg-amber-500/10 shrink-0 mt-0.5">
                <NotOnboardedShipIcon className="h-6 w-6 text-amber-500" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-foreground">Form Pending</span>
                <span className="text-xs text-muted-foreground">
                  User has visited the community, but onboarding questions/form are yet to be
                  submitted.
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3 p-2.5 rounded-lg border border-sky-500/20 bg-sky-500/5">
              <div className="p-2 rounded-md bg-sky-500/10 shrink-0 mt-0.5">
                <Users className="h-6 w-6 text-sky-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-foreground">Total Members</span>
                <span className="text-xs text-muted-foreground">
                  Total number of members in this community.
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3 p-2.5 rounded-lg border border-emerald-500/20 bg-emerald-500/5">
              <div className="p-2 rounded-md bg-emerald-500/10 shrink-0 mt-0.5">
                <ActiveBoatIcon className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-foreground">Active Member</span>
                <span className="text-xs text-muted-foreground">
                  Onboarded and active in the community within the last 30 days.
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3 p-2.5 rounded-lg border border-border/70 bg-muted/20">
              <div className="p-2 rounded-md bg-muted/60 shrink-0 mt-0.5">
                <InactiveAnchorIcon className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-foreground">Inactive (30+ Days)</span>
                <span className="text-xs text-muted-foreground">
                  Onboarded member with no activity recorded in the last 30 days.
                </span>
              </div>
            </div>

            {isPointsEnabled && (
              <div className="pt-2 border-t text-xs text-muted-foreground">
                The badge number next to the icon represents the member's{' '}
                <strong>Total Community Points</strong>.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
